import { Fragment, ReactElement, useEffect, useRef, useState } from 'react';

interface Props {
  data: any[] | null;
  renderer: (data: any, index: number) => ReactElement;
  initialCount?: number;
  loadCount?: number;
  container?: typeof Fragment;
  containerProps?: any;
  customViewport?: HTMLElement | null;
  scrollMult?: number;
  collapsedCount?: number;
}

const LAZY_LOAD_INITIAL_COUNT = 10;
const LAZY_LOAD_COUNT = 20;

const DEFAULT_SCROLL_MULTIPLY_NUMBER = 2.5;
const SCROLL_HEIGHT_MULTIPLY_NUMBER_TO_DETECT_AVAILABLE_SCROLL_HEIGHT = 3;

export const LazyLoad = ({
  data,
  renderer,
  initialCount = LAZY_LOAD_INITIAL_COUNT,
  loadCount = LAZY_LOAD_COUNT,
  container: Container = Fragment,
  containerProps = {},
  customViewport,
  scrollMult = DEFAULT_SCROLL_MULTIPLY_NUMBER,
  collapsedCount
}: Props): ReactElement => {
  const renderedCount = useRef(initialCount);
  const lazyElement = useRef<null | HTMLDivElement>(null);

  const [itemsToRender, setItemsToRender] = useState(
    (data ?? []).slice(0, renderedCount.current)
  );

  useEffect(() => {
    renderedCount.current = initialCount;
  }, [initialCount, loadCount, data?.length]);

  useEffect(() => {
    if (data) {
      setItemsToRender(data.slice(0, renderedCount.current));
    }

    let oldValue = 0;
    let newValue = 0;

    const viewport = customViewport || window;
    const clientHeight = (viewport as any).clientHeight * scrollMult;

    const onScroll = () => {
      newValue = (viewport as any).pageYOffset || (viewport as any).scrollTop;

      const lazyContanier = lazyElement.current?.closest(
        '[class*="ModuleWidgets"]'
      );

      if (oldValue < newValue && renderedCount.current <= (data?.length ?? 0)) {
        if (
          (!customViewport &&
            lazyContanier &&
            lazyContanier?.getBoundingClientRect().height <=
              (window.innerHeight + window.scrollY) * 2) ||
          (!lazyContanier &&
            document.documentElement.scrollHeight - window.scrollY <=
              window.innerHeight *
                SCROLL_HEIGHT_MULTIPLY_NUMBER_TO_DETECT_AVAILABLE_SCROLL_HEIGHT) ||
          (customViewport &&
            (viewport as any).scrollHeight - (viewport as any).scrollTop <=
              clientHeight)
        ) {
          if (data) {
            setItemsToRender(itemsToRender => [
              ...itemsToRender,
              ...data.slice(
                renderedCount.current,
                renderedCount.current + loadCount
              )
            ]);
          }

          renderedCount.current += loadCount;
        }
      }

      oldValue = newValue;
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [initialCount, loadCount, data, customViewport]);

  useEffect(() => {
    if (
      collapsedCount &&
      collapsedCount > 1 &&
      collapsedCount < (data?.length ?? 0) &&
      data
    ) {
      setItemsToRender(itemsToRender => [
        ...itemsToRender,
        ...data.slice(renderedCount.current, renderedCount.current + 1)
      ]);
      renderedCount.current += 1;
    }
  }, [collapsedCount]);

  return (
    <div className="lazyLoad__wrapper" ref={lazyElement}>
      <Container {...containerProps}>{itemsToRender.map(renderer)}</Container>
    </div>
  );
};
